import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  assignFreeSecretKeys,
  compileMobile,
  isPhoneBackgroundHost,
  testNameKey,
  uniqueTestName,
  type MobileRawAction,
  type MobileStep,
  type RecordingShotName,
  type RecordingTraffic,
  type RecordingView,
} from '@1qa/shared';
import { AlertTriangle, ArrowLeft, CheckCircle2, Image as ImageIcon, Plus, Save, Smartphone, Trash2, X, Zap } from '@/components/icons';
import { ApiError, api, errorMessage, fetchRecordingShot } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { buildOptionLabel } from '@/lib/runBuild';
import { phoneOptionLabel, useRunDevice } from '@/lib/runDevice';
import { Badge, Button, Card, CardBody, CardHeader, ErrorNotice, Field, Input, PageHeader, PageLoader, Select, useToast } from '@/components/ui';
import { EmulatorScreen } from '@/components/recording/EmulatorScreen';

/** How often the page asks 1QA for what the agent saw. */
const POLL_MS = 1000;
/** After Stop, AI names still on their way are asked for this often, for this long. */
const NAMES_POLL_MS = 2000;
const NAMES_WAIT_MS = 90_000;
/** Build choice: install nothing, use the app already on the phone. */
const ON_PHONE = '';
/** Device choice of one of 1QA's emulators (performance scripts): `emu:<profile>`. */
const EMU = 'emu:';

type Phase = { kind: 'setup' } | { kind: 'live'; id: string } | { kind: 'review'; id: string };
/** What the recording becomes: a functional test of this project, or a performance script (traffic recorded too). */
type Mode = 'test' | 'perf';
/** The pause between steps of a new script: 1 to 3 s, as the browser recorder's scripts start with. */
const DEFAULT_THINK_MS = { min: 1000, max: 3000 };

/**
 * Record on your phone (docs/design/mobile-recorder.md): the tester uses the app on a phone connected through the
 * Device Agent; the agent sends what it saw, this page turns it into steps, and after Stop the tester reviews them
 * and saves a test. In performance mode (`?mode=perf`, `?perf=<performance project>`) the agent also records the app's
 * network traffic through its recording proxy, and the review saves a performance script named after the steps.
 */
export function MobileRecordPage() {
  const { projectId = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const { can } = useAuth();
  const project = useQuery({ queryKey: ['project', projectId], queryFn: () => api.project(projectId) });
  const devices = useRunDevice(projectId, project.data?.platform);
  const builds = useQuery({ queryKey: ['builds', projectId], queryFn: () => api.builds(projectId), enabled: project.data?.platform === 'ANDROID' });
  const siblings = useQuery({ queryKey: ['tests', projectId], queryFn: () => api.tests(projectId) });
  const [params, setParams] = useSearchParams();
  const mode: Mode = params.get('mode') === 'perf' ? 'perf' : 'test';
  const orgId = project.data?.orgId;
  const orgProjects = useQuery({ queryKey: ['projects', orgId], queryFn: () => api.projects(orgId!), enabled: !!orgId && mode === 'perf' });
  const perfProjects = (orgProjects.data ?? []).filter((x) => x.platform === 'PERFORMANCE');
  const perfProjectId = params.get('perf') && perfProjects.some((x) => x.id === params.get('perf')) ? params.get('perf')! : (perfProjects[0]?.id ?? '');
  const perfProject = perfProjects.find((x) => x.id === perfProjectId) ?? null;
  const scripts = useQuery({ queryKey: ['perf-scripts', perfProjectId], queryFn: () => api.perfScripts(perfProjectId), enabled: mode === 'perf' && !!perfProjectId });
  const serverCerts = useQuery({ queryKey: ['server-certs', perfProjectId], queryFn: () => api.serverCerts(perfProjectId), enabled: mode === 'perf' && !!perfProjectId });
  // 1QA's emulators (Android runners) record performance scripts with nothing to set up on a phone or in the app.
  const emulators = useQuery({ queryKey: ['recording-emulators', projectId], queryFn: () => api.recordingEmulators(projectId), enabled: mode === 'perf' && project.data?.platform === 'ANDROID' });
  const emulatorList = useMemo(() => (mode === 'perf' ? (emulators.data ?? []) : []), [mode, emulators.data]);
  const certHosts = (serverCerts.data ?? []).filter((c) => !c.expired).flatMap((c) => c.hosts);
  const appCovered = (serverCerts.data ?? []).some((c) => !c.expired && c.coversAppHost);
  const setParam = (key: string, value: string | null) =>
    setParams(
      (prev) => {
        const n = new URLSearchParams(prev);
        if (value) n.set(key, value);
        else n.delete(key);
        return n;
      },
      { replace: true },
    );

  const [phase, setPhase] = useState<Phase>({ kind: 'setup' });
  const [device, setDevice] = useState('');
  const [buildId, setBuildId] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [rec, setRec] = useState<RecordingView | null>(null);
  const [actions, setActions] = useState<MobileRawAction[]>([]);
  const [checks, setChecks] = useState<MobileRawAction[]>([]);
  const [names, setNames] = useState<Record<string, RecordingShotName>>({});

  const phones = devices.phones;
  const readyPhones = phones.filter((p) => !p.agent.paused && p.device.status === 'ready');
  // Preselect: the phone runs use when it is ready, else the first ready one; the newest build when there is one.
  // Performance scripts: an emulator first (nothing to set up); functional tests record on phones only.
  const onEmulator = mode === 'perf' && device.startsWith(EMU);
  useEffect(() => {
    if (mode !== 'perf' && device.startsWith(EMU)) return setDevice('');
    if (device) return;
    if (emulatorList[0]) return setDevice(EMU + emulatorList[0].id);
    if (!readyPhones.length) return;
    setDevice(readyPhones.find((p) => p.device.profile === devices.deviceProfile)?.device.profile ?? readyPhones[0]!.device.profile);
  }, [mode, device, emulatorList, readyPhones, devices.deviceProfile]);
  useEffect(() => {
    if (buildId === null && builds.data) setBuildId(builds.data[0]?.id ?? ON_PHONE);
    // An emulator has no app of its own: it needs a build.
    else if (onEmulator && buildId === ON_PHONE && builds.data?.[0]) setBuildId(builds.data[0].id);
  }, [buildId, builds.data, onEmulator]);

  // While recording: ask for new actions every second; the review starts when the agent has sent the last of them.
  const live = phase.kind === 'live' ? phase.id : null;
  const next = useRef(0);
  useEffect(() => {
    if (!live) return;
    let stop = false;
    const tick = async () => {
      try {
        const v = await api.recording(live, next.current);
        if (stop) return;
        next.current = v.next;
        setRec(v);
        setNames(v.names);
        if (v.actions.length) setActions((a) => [...a, ...v.actions]);
        if (v.status === 'stopped') return setPhase({ kind: 'review', id: live });
        if (v.status === 'failed' || v.status === 'cancelled') return;
      } catch (err) {
        if (stop) return;
        if (err instanceof ApiError && err.status === 404) return setRec((r) => (r ? { ...r, status: 'failed', error: 'The recording has ended.' } : r));
      }
      if (!stop) timer = setTimeout(tick, POLL_MS);
    };
    let timer = setTimeout(tick, 0);
    return () => {
      stop = true;
      clearTimeout(timer);
    };
  }, [live]);

  // Leaving the page while recording cancels the recording, so the phone is free for tests again.
  const active = live && (rec?.status === 'starting' || rec?.status === 'recording') ? live : null;
  useEffect(() => {
    if (!active) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => {
      window.removeEventListener('beforeunload', warn);
      void api.cancelRecording(active).catch(() => undefined);
    };
  }, [active]);

  const compiled = useMemo(() => compileMobile([...actions, ...checks], names), [actions, checks, names]);

  // Taps without a name are named by the project's AI model from their screenshot; after Stop the last names may
  // still be on their way.
  const naming = actions.some((a) => a.kind === 'tap' && a.shot && !names[a.shot]);
  const reviewing = phase.kind === 'review' ? phase.id : null;
  useEffect(() => {
    if (!reviewing || !naming) return;
    const until = Date.now() + NAMES_WAIT_MS;
    const timer = setInterval(() => {
      if (Date.now() > until) return clearInterval(timer);
      void api.recording(reviewing, next.current).then((v) => setNames(v.names), () => undefined);
    }, NAMES_POLL_MS);
    return () => clearInterval(timer);
  }, [reviewing, naming]);

  if (project.isLoading) return <PageLoader />;
  if (project.error) return <ErrorNotice error={project.error} />;
  const p = project.data!;
  const back = `/projects/${projectId}`;

  const start = async () => {
    setStarting(true);
    setProblem(null);
    try {
      const target = onEmulator ? { emulator: device.slice(EMU.length) } : { device };
      const v = await api.startRecording(projectId, { ...target, buildId: buildId || null, traffic: mode === 'perf', ...(mode === 'perf' && perfProjectId && { perfProjectId }) });
      next.current = 0;
      setActions([]);
      setChecks([]);
      setNames({});
      setRec(v);
      setPhase({ kind: 'live', id: v.id });
    } catch (err) {
      setProblem(errorMessage(err));
    } finally {
      setStarting(false);
    }
  };

  const restart = () => {
    setPhase({ kind: 'setup' });
    setRec(null);
  };

  /**
   * Performance mode: the typed passwords become test data of the performance project first (so 1QA turns them into
   * {{KEY}} references in the traffic), then 1QA correlates the traffic, named after the reviewed steps. The script is
   * saved at once unless 1QA found something to check first: then its review opens.
   */
  const savePerfScript = async ({ name, steps, values }: { name: string; steps: MobileStep[]; values: Record<string, string> }) => {
    if (!rec || !perfProjectId) return;
    const [projectVars, orgVars] = await Promise.all([api.projectVariables(perfProjectId), api.orgVariables(p.orgId).catch(() => [])]);
    const { steps: keyed, keys } = assignFreeSecretKeys(steps, compiled.secrets.filter((s) => steps.some((st) => st.value.includes(`{{${s.key}}}`))), [...projectVars, ...orgVars].map((v) => v.key));
    for (const s of compiled.secrets) {
      const key = keys.get(s.key);
      if (!key) continue;
      await api.createProjectVariable(perfProjectId, { key, value: values[s.key] ?? '', isSecret: false, description: `Recorded from the "${s.label}" field on a phone` });
    }
    const capture = await api.recordingPerfScript(rec.id, { perfProjectId, name, steps: keyed.map((s) => ({ kind: s.kind, value: s.value, ...(s.ts !== undefined && { ts: s.ts }) })) });
    // The traffic (it holds what was typed) is not needed any more: the capture has the script.
    await api.cancelRecording(rec.id).catch(() => undefined);
    qc.invalidateQueries({ queryKey: ['project-variables', perfProjectId] });
    const reviewUrl = `/projects/${perfProjectId}/perf/scripts/new?capture=${encodeURIComponent(capture.id)}`;
    const review = capture.review;
    if (!review || review.problems.length) {
      toast.success('Recorded. 1QA needs you to check a few things before it becomes a script.');
      return navigate(reviewUrl);
    }
    try {
      const script = { ...review.script, transactions: review.script.transactions.map(({ thinkTimeMs: _recorded, ...t }) => ({ ...t, thinkTimeMs: DEFAULT_THINK_MS })) };
      const saved = await api.createPerfScript(perfProjectId, { name, script, source: 'recorder' });
      qc.invalidateQueries({ queryKey: ['perf-scripts', perfProjectId] });
      toast.success(`Script “${saved.name}” saved. Run its check to try it with one user.`);
      navigate(`/projects/${perfProjectId}/perf/scripts/${saved.id}`);
    } catch (err) {
      toast.error(`The script could not be saved (${errorMessage(err)}). Finish it on the review page.`);
      navigate(reviewUrl);
    }
  };

  const header = (
    <PageHeader
      eyebrow={
        <Link to={back} className="inline-flex items-center gap-1 hover:underline">
          <ArrowLeft className="size-3" /> {p.name}
        </Link>
      }
      title={mode === 'perf' ? 'Record a performance script' : 'Record on your phone'}
      description={
        mode === 'perf'
          ? 'Use your app on one of 1QA’s emulators (right here in the browser) or on a real phone: 1QA records the requests it sends and turns them into a performance script, one transaction per step.'
          : 'Use your app on a real phone: 1QA turns your taps, typing and scrolling into plain-English steps.'
      }
    />
  );

  if (p.platform !== 'ANDROID') {
    return (
      <div className="mx-auto max-w-3xl">
        {header}
        <ErrorNotice error={new Error('Recording on a phone works for Android projects only (for now).')} />
      </div>
    );
  }
  if (!can('test.write', p.orgId)) {
    return (
      <div className="mx-auto max-w-3xl">
        {header}
        <ErrorNotice error={new Error('Only people who can write tests in this project can record one.')} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {header}
      {phase.kind === 'setup' && (
        <Card>
          <CardHeader
            title={mode === 'perf' ? '1. Choose where to record' : '1. Choose the phone'}
            description={
              onEmulator
                ? 'One of 1QA’s emulators: you use the app on its screen here in the browser. Nothing to install on a phone or change in the app.'
                : `A phone connected to a computer that runs the 1QA Device Agent (version ${mode === 'perf' ? '1.4.0' : '1.2.2'} or newer).`
            }
          />
          <CardBody className="space-y-5">
            <Field label="What to record" hint={mode === 'perf' ? 'The requests your app sends become a script in a performance project. No functional test is saved.' : 'Your steps become a test in this project.'}>
              {(id) => (
                <Select
                  id={id}
                  value={mode}
                  onChange={(e) => setParam('mode', e.target.value === 'perf' ? 'perf' : null)}
                  options={[
                    { value: 'test', label: 'Functional test' },
                    { value: 'perf', label: 'Performance script (records network traffic)' },
                  ]}
                />
              )}
            </Field>
            {mode === 'perf' &&
              (orgProjects.isLoading ? null : perfProjects.length === 0 ? (
                <p className="rounded-field bg-warning-subtle p-3 text-sm text-fg ring-1 ring-warning/40 ring-inset">
                  Scripts are saved in a performance project, and this organization has none you can use yet. Create one under Test projects › Performance, with your app’s API address as its app address.
                </p>
              ) : (
                <Field label="Performance project" hint={perfProject?.targetUrl ? `Requests to ${perfProject.targetUrl} (and its allowed load targets) go into the script.` : undefined}>
                  {(id) => <Select id={id} value={perfProjectId} onChange={(e) => setParam('perf', e.target.value)} options={perfProjects.map((x) => ({ value: x.id, label: x.name }))} />}
                </Field>
              ))}
            {phones.length === 0 && !emulatorList.length ? (
              <p className="rounded-field bg-warning-subtle p-3 text-sm text-fg ring-1 ring-warning/40 ring-inset">
                No phone is connected. Plug your phone into your computer, start the 1QA Device Agent there, and it shows up here.{' '}
                <Link to="/helpers/devices" className="text-link hover:underline">
                  Helper services › Devices
                </Link>{' '}
                explains how.
              </p>
            ) : (
              <Field
                label={emulatorList.length ? 'Record on' : 'Phone'}
                hint={
                  onEmulator
                    ? 'The build is installed on a fresh emulator, which starts afresh again after the recording. The first start of a large app can take a minute or two.'
                    : readyPhones.length
                      ? 'The app starts on this phone with its data cleared, like at the start of every test.'
                      : 'No phone is ready right now: it may be busy with a test, or waiting for you to allow USB debugging.'
                }
              >
                {(id) => (
                  <Select
                    id={id}
                    value={device}
                    onChange={(e) => setDevice(e.target.value)}
                    options={readyPhones.length || emulatorList.length ? [] : [{ value: '', label: 'No phone is ready' }]}
                    groups={[
                      ...(emulatorList.length ? [{ label: '1QA emulators (in the browser)', options: emulatorList.map((e) => ({ value: EMU + e.id, label: `${e.label}${e.ready ? '' : ' (busy or starting)'}` })) }] : []),
                      ...(phones.length ? [{ label: 'Connected phones', options: phones.map(({ agent, device: d }) => ({ value: d.profile, label: phoneOptionLabel(agent, d) })) }] : []),
                    ]}
                  />
                )}
              </Field>
            )}
            <Field
              label="App"
              hint={
                onEmulator
                  ? builds.data?.length
                    ? `Recording ${p.appId ?? 'the app'} from this build.`
                    : 'Upload your app’s build (APK) under the project’s Builds first: the emulator installs it.'
                  : `Recording ${p.appId ?? 'the app'}. Installing a build replaces the app on the phone.`
              }
            >
              {(id) => (
                <Select
                  id={id}
                  value={buildId ?? ON_PHONE}
                  onChange={(e) => setBuildId(e.target.value)}
                  options={onEmulator ? [] : [{ value: ON_PHONE, label: 'The app already on the phone' }]}
                  groups={[{ label: 'Install a build', options: (builds.data ?? []).map((b) => ({ value: b.id, label: buildOptionLabel(b) })) }]}
                />
              )}
            </Field>
            {mode === 'perf' && !onEmulator && <TrafficSetup certHosts={certHosts} appCovered={appCovered} />}
            <div className="rounded-field bg-surface-sunken p-4 text-sm text-fg-secondary">
              <p className="font-medium text-fg">How it works</p>
              <ol className="mt-2 list-decimal space-y-1 pl-5">
                <li>{onEmulator ? 'Press Start. The emulator’s screen shows up here and the app opens on it.' : 'Press Start. The app opens on the phone.'}</li>
                <li>
                  {onEmulator
                    ? 'Use the app on that screen: click to tap, drag to scroll, and type into the box below it. The steps show up as you go.'
                    : 'Use the app on the phone. Tap, type and scroll the way your users do. The steps show up here as you go.'}
                </li>
                <li>
                  To check that something is shown, press <strong>Add a check</strong> here and pick the text.
                </li>
                <li>{mode === 'perf' ? 'Press Stop, look over the steps (they name the script’s transactions), and save the script.' : 'Press Stop, look over the steps, and save the test.'}</li>
              </ol>
            </div>
            {problem && (
              <p role="alert" className="rounded-field bg-warning-subtle p-3 text-sm text-fg ring-1 ring-warning/40 ring-inset">
                {problem}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => navigate(back)}>
                Cancel
              </Button>
              <Button
                icon={mode === 'perf' ? <Zap className="size-3" /> : <Smartphone className="size-3" />}
                onClick={start}
                loading={starting}
                disabled={
                  !device ||
                  (onEmulator ? !buildId : !readyPhones.some((x) => x.device.profile === device)) ||
                  (mode === 'perf' && !perfProjectId)
                }
              >
                Start recording
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {phase.kind === 'live' && rec && (
        <LivePanel
          rec={rec}
          steps={compiled.steps}
          onProblem={(m) => toast.error(m)}
          onCheck={(text) => setChecks((c) => [...c, { kind: 'assert', text, ts: Math.max(0, ...actions.map((a) => a.ts), ...c.map((x) => x.ts)) + 1 }])}
          onStop={async () => {
            try {
              await api.stopRecording(rec.id);
              setRec({ ...rec, status: 'recording', error: null });
              toast.success('Stopping: 1QA reads the last of your typing from the phone…');
            } catch (err) {
              toast.error(errorMessage(err));
            }
          }}
          onCancel={async () => {
            await api.cancelRecording(rec.id).catch(() => undefined);
            restart();
          }}
          onAgain={restart}
        />
      )}

      {phase.kind === 'review' && rec && (
        <Review
          rec={rec}
          initial={compiled.steps}
          latest={compiled.steps}
          secrets={compiled.secrets}
          takenNames={mode === 'perf' ? (scripts.data ?? []).map((x) => x.name) : (siblings.data ?? []).map((t) => t.name)}
          perf={mode === 'perf' && perfProject ? { projectName: perfProject.name } : undefined}
          onDiscard={async () => {
            await api.cancelRecording(rec.id).catch(() => undefined);
            restart();
          }}
          onSave={async ({ name, steps, values }) => {
            if (mode === 'perf') return savePerfScript({ name, steps, values });
            const [projectVars, orgVars] = await Promise.all([api.projectVariables(projectId), api.orgVariables(p.orgId).catch(() => [])]);
            const { steps: keyed, keys } = assignFreeSecretKeys(steps, compiled.secrets.filter((s) => steps.some((st) => st.value.includes(`{{${s.key}}}`))), [...projectVars, ...orgVars].map((v) => v.key));
            for (const s of compiled.secrets) {
              const key = keys.get(s.key);
              if (!key) continue;
              // Plain test data, as the browser recorder saves passwords; mark it secret on the Test data page.
              await api.createProjectVariable(projectId, { key, value: values[s.key] ?? '', isSecret: false, description: `Recorded from the "${s.label}" field on a phone` });
            }
            const test = await api.createTest(projectId, { name, steps: keyed.map((s, i) => ({ id: `s${i + 1}`, kind: s.kind, value: s.value })), tags: ['recorded'] });
            await api.cancelRecording(rec.id).catch(() => undefined);
            qc.invalidateQueries({ queryKey: ['tests', projectId] });
            qc.invalidateQueries({ queryKey: ['project-variables', projectId] });
            toast.success(`“${test.name}” saved. Run it to check it.`);
            navigate(`/projects/${projectId}/tests/${test.id}`);
          }}
        />
      )}
    </div>
  );
}

function StepList({ steps }: { steps: MobileStep[] }) {
  if (!steps.length) return <p className="text-sm text-fg-muted">No steps yet.</p>;
  return (
    <ol className="space-y-2" aria-live="polite">
      {steps.map((s, i) => (
        <li key={s.id} className="flex items-start gap-3 rounded-field bg-surface-sunken px-3 py-2 text-sm">
          <span className="w-5 shrink-0 text-right text-fg-muted tabular-nums">{i + 1}</span>
          <Badge tone={s.kind === 'assert' ? 'success' : s.kind === 'open' ? 'info' : 'neutral'}>{s.kind === 'open' ? 'open app' : s.kind}</Badge>
          <span className="min-w-0 flex-1 break-words text-fg">{s.value || 'Start the app'}</span>
          {s.shot && <ImageIcon className="mt-0.5 size-3 shrink-0 text-fg-muted" aria-label="Has a screenshot" />}
          {s.check && <AlertTriangle className="mt-0.5 size-3 shrink-0 text-warning" aria-label={s.check} />}
        </li>
      ))}
    </ol>
  );
}

function LivePanel({
  rec,
  steps,
  onProblem,
  onCheck,
  onStop,
  onCancel,
  onAgain,
}: {
  rec: RecordingView;
  steps: MobileStep[];
  onProblem: (message: string) => void;
  onCheck: (text: string) => void;
  onStop: () => void;
  onCancel: () => void;
  onAgain: () => void;
}) {
  const [checking, setChecking] = useState(false);
  const [own, setOwn] = useState('');
  const [stopping, setStopping] = useState(false);
  const ended = rec.status === 'failed' || rec.status === 'cancelled';
  const emulator = rec.target === 'emulator';
  return (
    <Card>
      <CardHeader
        title={
          <span className="inline-flex items-center gap-2">
            {rec.status === 'recording' && <span className="size-2.5 animate-pulse rounded-full bg-danger" aria-hidden />}
            {rec.status === 'starting' ? 'Starting the app…' : rec.status === 'recording' ? (stopping ? 'Stopping…' : 'Recording') : rec.status === 'cancelled' ? 'Cancelled' : 'Recording stopped'}
          </span>
        }
        description={`${rec.deviceLabel} · ${rec.appId}`}
        actions={
          !ended && (
            <>
              <Button variant="ghost" size="sm" icon={<X className="size-3" />} onClick={onCancel}>
                Cancel
              </Button>
              <Button
                size="sm"
                variant="danger"
                loading={stopping}
                disabled={rec.status !== 'recording'}
                onClick={() => {
                  setStopping(true);
                  onStop();
                }}
              >
                Stop
              </Button>
            </>
          )
        }
      />
      <CardBody className="space-y-4">
        {rec.status === 'starting' && (
          <p className="text-sm text-fg-secondary">
            {emulator
              ? '1QA is getting an emulator ready and installing your build. A large app can take a minute or two to show its first screen.'
              : 'The Device Agent is getting the phone ready (installing the build can take a minute). Keep the phone unlocked.'}
          </p>
        )}
        {rec.status === 'recording' && !stopping && (
          <p className="text-sm text-fg-secondary">
            {emulator
              ? 'Use the app on the screen below: click to tap, drag to scroll, type into the box under it. The screen follows a moment behind, so wait for each screen to load.'
              : 'Use the app on the phone now. Wait a moment after each screen loads, so 1QA can read it.'}
          </p>
        )}
        {emulator && !ended && <EmulatorScreen recordingId={rec.id} active={rec.status === 'recording' && !stopping} onProblem={onProblem} />}
        {ended && (
          <div role="alert" className="space-y-3 rounded-field bg-warning-subtle p-3 text-sm text-fg ring-1 ring-warning/40 ring-inset">
            <p>{rec.error ?? 'The recording was cancelled.'}</p>
            <Button size="sm" variant="secondary" onClick={onAgain}>
              Record again
            </Button>
          </div>
        )}
        {rec.traffic && <TrafficStatus traffic={rec.traffic} recording={rec.status === 'recording'} steps={steps.length} emulator={emulator} />}
        <StepList steps={steps} />
        {rec.status === 'recording' && !stopping && (
          <div className="space-y-2">
            {!checking ? (
              <Button size="sm" variant="secondary" icon={<Plus className="size-3" />} onClick={() => setChecking(true)}>
                Add a check
              </Button>
            ) : (
              <div className="space-y-3 rounded-field p-3 ring-1 ring-border ring-inset">
                <p className="text-sm font-medium text-fg">Which text must the screen show at this point?</p>
                {rec.texts.length ? (
                  <div className="flex max-h-48 flex-wrap gap-2 overflow-y-auto">
                    {rec.texts.map((t) => (
                      <button
                        key={t}
                        type="button"
                        className="rounded-full bg-primary-subtle px-3 py-1 text-xs text-primary hover:bg-primary-subtle-hover"
                        onClick={() => {
                          onCheck(t);
                          setChecking(false);
                        }}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-fg-muted">1QA has not read any text on this screen yet.</p>
                )}
                <form
                  className="flex gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!own.trim()) return;
                    onCheck(own.trim());
                    setOwn('');
                    setChecking(false);
                  }}
                >
                  <Input value={own} onChange={(e) => setOwn(e.target.value)} placeholder="Or type the text" aria-label="Text to check" />
                  <Button type="submit" size="sm" disabled={!own.trim()}>
                    Add
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setChecking(false)}>
                    Close
                  </Button>
                </form>
              </div>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function Review({
  rec,
  initial,
  latest,
  secrets,
  takenNames,
  perf,
  onDiscard,
  onSave,
}: {
  rec: RecordingView;
  /** Performance mode: the script goes to this performance project. */
  perf?: { projectName: string };
  initial: MobileStep[];
  /** The steps as compiled now: AI names arrive after Stop and replace the steps the tester has not changed. */
  latest: MobileStep[];
  secrets: { key: string; label: string }[];
  takenNames: string[];
  onDiscard: () => void;
  onSave: (r: { name: string; steps: MobileStep[]; values: Record<string, string> }) => Promise<void>;
}) {
  const [steps, setSteps] = useState(initial);
  const edited = useRef(new Set<string>());
  useEffect(() => {
    setSteps((all) => all.map((s) => (edited.current.has(s.id) ? s : (latest.find((l) => l.id === s.id) ?? s))).filter((s) => edited.current.has(s.id) || latest.some((l) => l.id === s.id)));
  }, [latest]);
  const [name, setName] = useState(() => uniqueTestName(`Recorded on ${rec.deviceLabel.split(' · ')[0]}`, takenNames));
  const [values, setValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const used = secrets.filter((s) => steps.some((st) => st.value.includes(`{{${s.key}}}`)));
  const nameTaken = takenNames.some((t) => testNameKey(t) === testNameKey(name));
  const missing = used.filter((s) => !values[s.key]);
  const runnable = steps.filter((s) => s.kind === 'open' || s.value.trim());
  const blocked = !name.trim() || nameTaken || missing.length > 0 || runnable.length === 0;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave({ name: name.trim(), steps: runnable.map((s) => ({ ...s, value: s.value.trim() })), values });
    } catch (err) {
      setError(errorMessage(err));
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title={
          <span className="inline-flex items-center gap-2">
            <CheckCircle2 className="size-4 text-success" /> Review the recorded steps
          </span>
        }
        description={
          perf
            ? `${steps.length} steps from ${rec.deviceLabel}. Each step names the requests sent after it (a transaction). Change the wording, remove what you don't need, then save the script to ${perf.projectName}.`
            : `${steps.length} steps from ${rec.deviceLabel}. Change any step's wording, remove what you don't need, then save.`
        }
      />
      <CardBody className="space-y-5">
        {perf && rec.traffic && <TrafficStatus traffic={rec.traffic} recording={false} steps={steps.length} emulator={rec.target === 'emulator'} />}
        <Field label={perf ? 'Script name' : 'Test name'} error={nameTaken ? (perf ? 'A script with this name already exists in that project.' : 'A test with this name already exists in this project.') : undefined}>
          {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} maxLength={200} />}
        </Field>
        <ol className="space-y-2">
          {steps.map((s, i) => (
            <li key={s.id} className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="w-5 shrink-0 text-right text-sm text-fg-muted tabular-nums">{i + 1}</span>
                <Badge tone={s.kind === 'assert' ? 'success' : s.kind === 'open' ? 'info' : 'neutral'}>{s.kind === 'open' ? 'open app' : s.kind}</Badge>
                {s.kind === 'open' ? (
                  <span className="flex-1 text-sm text-fg-secondary">Start the app</span>
                ) : (
                  <Input
                    value={s.value}
                    aria-label={`Step ${i + 1}`}
                    onChange={(e) => {
                      edited.current.add(s.id);
                      setSteps((all) => all.map((x) => (x.id === s.id ? { ...x, value: e.target.value, check: undefined } : x)));
                    }}
                    className="flex-1 py-2"
                  />
                )}
                <Button variant="ghost" size="sm" aria-label={`Remove step ${i + 1}`} icon={<Trash2 className="size-3" />} onClick={() => setSteps((all) => all.filter((x) => x.id !== s.id))} />
              </div>
              {s.check && (
                <p className="ml-7 flex items-center gap-1.5 text-xs text-warning">
                  <AlertTriangle className="size-3 shrink-0" aria-hidden /> {s.check}
                </p>
              )}
              {s.shot && <ShotPreview recordingId={rec.id} shotId={s.shot} step={i + 1} />}
            </li>
          ))}
        </ol>
        {used.length > 0 && (
          <div className="space-y-3 rounded-field bg-surface-sunken p-4">
            <p className="text-sm text-fg">
              Phones never show what is typed into password fields. Type the value once here. It is saved as test data in {perf ? `${perf.projectName}, so the script sends it` : 'this project'}, and you can mark it secret on the Test data page.
            </p>
            {used.map((s) => (
              <Field key={s.key} label={`${s.label} ({{${s.key}}})`}>
                {(id) => <Input id={id} type="password" autoComplete="off" value={values[s.key] ?? ''} onChange={(e) => setValues((v) => ({ ...v, [s.key]: e.target.value }))} />}
              </Field>
            ))}
          </div>
        )}
        {error && (
          <p role="alert" className="rounded-field bg-danger-subtle p-3 text-sm text-danger-text">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onDiscard} disabled={saving}>
            Discard and record again
          </Button>
          <Button icon={<Save className="size-3" />} onClick={save} loading={saving} disabled={blocked || (!!perf && rec.traffic?.requests === 0)}>
            {perf ? 'Save script' : 'Save test'}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

/** A tap's screenshot (the red ring marks the touch): small, larger on click. */
function ShotPreview({ recordingId, shotId, step }: { recordingId: string; shotId: string; step: number }) {
  const [big, setBig] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let gone = false;
    let made: string | null = null;
    fetchRecordingShot(recordingId, shotId).then(
      (blob) => {
        if (gone) return;
        made = URL.createObjectURL(blob);
        setUrl(made);
      },
      () => undefined,
    );
    return () => {
      gone = true;
      if (made) URL.revokeObjectURL(made);
    };
  }, [recordingId, shotId]);
  if (!url) return null;
  return (
    <button type="button" className="ml-7 block" onClick={() => setBig((b) => !b)} aria-label={big ? `Make the screenshot of step ${step} smaller` : `Show the screenshot of step ${step} larger`}>
      <img src={url} alt={`The screen at step ${step}; the red ring marks the tap`} className={`${big ? 'w-72' : 'w-20'} rounded-field border border-border`} />
    </button>
  );
}

/**
 * What a performance recording needs on the phone, shown before Start. Hosts the performance project has its own server
 * certificate for need nothing; others need the 1QA certificate and a build that trusts it.
 */
function TrafficSetup({ certHosts, appCovered }: { certHosts: string[]; appCovered: boolean }) {
  if (appCovered) {
    return (
      <div className="space-y-2 rounded-field bg-success-subtle p-4 text-sm text-fg ring-1 ring-border ring-inset">
        <p className="font-medium">Nothing to install on the phone</p>
        <p className="text-fg-secondary">
          This performance project has your server’s own certificate for {certHosts.join(', ')}, so any build of your app can be recorded as it is, release builds
          included. Requests to other hosts are passed through unrecorded unless the phone trusts the 1QA certificate.
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-2 rounded-field bg-info-subtle p-4 text-sm text-fg ring-1 ring-border ring-inset">
      <p className="font-medium">Before you record traffic</p>
      <p className="text-fg-secondary">
        Easiest: a Project Admin adds your test API’s certificate under the performance project’s Settings › Server certificates; then nothing below is needed.
        {certHosts.length > 0 && ` (It has one for ${certHosts.join(', ')}, but not for its app address.)`} Otherwise:
      </p>
      <ol className="list-decimal space-y-1 pl-5 text-fg-secondary">
        <li>
          The phone must trust the 1QA certificate of the computer the Device Agent runs on. When you press Start, the agent copies it to the phone as{' '}
          <code className="font-mono text-xs">Download/1QA-recorder-CA.crt</code>. Install it once: Settings › Security › Encryption &amp; credentials › Install a certificate › CA certificate (the
          names differ a little between phones).
        </li>
        <li>
          Use a debug or test build of your app that trusts certificates the user installed (its <code className="font-mono text-xs">network_security_config</code> lists{' '}
          <code className="font-mono text-xs">&lt;certificates src="user" /&gt;</code>). Release builds and apps that pin their certificates send their requests past 1QA: 1QA names those hosts.
        </li>
        <li>While recording, the phone sends its traffic through the agent’s computer. 1QA switches that off again when you stop, cancel, or close the agent.</li>
      </ol>
    </div>
  );
}

/** Requests recorded so far, and hosts the app would not open for 1QA. */
function TrafficStatus({ traffic, recording, steps, emulator = false }: { traffic: RecordingTraffic; recording: boolean; steps: number; emulator?: boolean }) {
  const none = traffic.requests === 0 && steps > 1;
  // The proxy is set for the whole phone: Android, Google services and SDKs refuse 1QA's certificate too. Only the
  // other hosts are the app's problem (agents before 1.4.1 report the background hosts as refused as well).
  const refused = traffic.refused.filter((r) => !isPhoneBackgroundHost(r.host));
  const background = [...new Set([...(traffic.background ?? []), ...traffic.refused.filter((r) => isPhoneBackgroundHost(r.host)).map((r) => r.host)])];
  return (
    <div className="space-y-2" aria-live="polite">
      <p className="flex items-center gap-2 text-sm text-fg">
        <Zap className="size-3 text-primary" aria-hidden />
        <span>
          <strong className="tabular-nums">{traffic.requests}</strong> {traffic.requests === 1 ? 'request' : 'requests'} recorded
        </span>
      </p>
      {!!traffic.ownCertHosts?.length && <p className="text-xs text-fg-muted">Opened with your server’s own certificate: {traffic.ownCertHosts.join(', ')}.</p>}
      {refused.length > 0 && (
        <p role="alert" className="rounded-field bg-warning-subtle p-3 text-sm text-fg ring-1 ring-warning/40 ring-inset">
          The app does not trust 1QA’s certificate for {refused.map((r) => r.host).join(', ')}, so those requests are not recorded (they still reach the server). Install the certificate
          {traffic.certOnPhone ? ` (${traffic.certOnPhone} on the phone)` : ''} and use a build that trusts user certificates, or leave those hosts out of the script.
        </p>
      )}
      {background.length > 0 && (
        <p className="text-xs text-fg-muted">
          The phone’s own background traffic is not recorded (this is normal; it is never part of a script): {background.join(', ')}.
        </p>
      )}
      {none && (
        <p role="alert" className="rounded-field bg-warning-subtle p-3 text-sm text-fg ring-1 ring-warning/40 ring-inset">
          {recording ? 'No requests yet. ' : 'No requests were recorded. '}
          {emulator
            ? 'The app has not sent anything over HTTPS or HTTP yet. Requests on other ports and WebSocket messages are not recorded.'
            : 'Check that the phone trusts 1QA’s certificate and that the app is a build that trusts user certificates. Apps built with Flutter ignore the phone’s proxy setting: record them on a 1QA emulator instead.'}
        </p>
      )}
    </div>
  );
}
